import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "../i18n/useTranslation";
import { useUiLocale } from "../context/UiLocaleContext";
import { useAuth } from "../context/AuthContext";
import { resolveCoverImageUrl, resolveStoryImageUrl } from "../utils/presignedImageUrl";
import { coverDisplayTitleFromOverlays } from "../utils/coverOverlayTitle";
import LogoLoader from "../components/common/LogoLoader";
import { API_BASE_URL } from "../config/apiBaseUrl";
import DigitalBookReader from "../components/Wizard/DigitalBookReader";
import { buildBookReaderViews, type BookPage } from "../components/Wizard/bookPageModel";
import { coerceStoryPageNumber, findStoryPageSlotImage } from "../utils/storyPageImageMatch";

interface PageImage {
  pageNumber: number;
  isSidePicture?: boolean;
  editedImageUrl?: string;
  uploadedImageUrl?: string;
  imageUrl?: string;
  editedImageStorageKey?: string;
  uploadedImageStorageKey?: string;
  imageStorageKey?: string;
  accessibleDescription?: string;
}

interface CoverData {
  editedImageUrl?: string;
  uploadedImageUrl?: string;
  imageUrl?: string;
  editedImageStorageKey?: string;
  uploadedImageStorageKey?: string;
  imageStorageKey?: string;
  textOverlays?: unknown[];
  accessibleDescription?: string;
}

interface StoryData {
  sessionId: string;
  language: string;
  status?: string;
  submissionStatus?: string;
  frontCover?: CoverData;
  backCover?: CoverData;
  pageImages?: PageImage[];
  generatedStory?: string;
}

const DigitalBookViewPage: React.FC = () => {
  const { t } = useTranslation();
  const { uiLocale, setUiLocale } = useUiLocale();
  const { getAuthHeader, token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const sessionIdFromQuery = useMemo(
    () => new URLSearchParams(location.search).get("sessionId")?.trim() || "",
    [location.search]
  );

  const [storyData, setStoryData] = useState<StoryData | null>(null);
  const [pageSequence, setPageSequence] = useState<BookPage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [bookDisplayReady, setBookDisplayReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const readingLanguage =
    storyData?.language === "Hebrew"
      ? "Hebrew"
      : storyData?.language
        ? "English"
        : uiLocale === "he"
          ? "Hebrew"
          : "English";

  useEffect(() => {
    if (!sessionIdFromQuery) {
      navigate("/create/order-summary", { replace: true });
      return;
    }

    const fetchStoryData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_BASE_URL}/stories/${encodeURIComponent(sessionIdFromQuery)}`, {
          headers: { ...getAuthHeader() },
        });
        if (!response.ok) {
          throw new Error("Failed to load story data");
        }
        const data = await response.json();
        const story = data.data;
        if (!story) throw new Error("Story not found");

        setStoryData({
          sessionId: story.sessionId,
          language: story.language,
          status: story.status,
          submissionStatus: story.submissionStatus,
          frontCover: story.frontCover,
          backCover: story.backCover,
          pageImages: story.pageImages || [],
          generatedStory: story.generatedStory,
        });
      } catch (err: unknown) {
        console.error("DigitalBookViewPage: fetch failed", err);
        setError(err instanceof Error ? err.message : "Failed to load");
      } finally {
        setIsLoading(false);
      }
    };

    fetchStoryData();
  }, [sessionIdFromQuery, navigate, getAuthHeader]);

  const refetchStoryForBook = useCallback(async () => {
    if (!sessionIdFromQuery) return;
    try {
      const response = await fetch(`${API_BASE_URL}/stories/${encodeURIComponent(sessionIdFromQuery)}`, {
        headers: { ...getAuthHeader() },
      });
      if (!response.ok) return;
      const data = await response.json();
      const story = data?.data;
      if (!story) return;
      setStoryData({
        sessionId: story.sessionId,
        language: story.language,
        status: story.status,
        submissionStatus: story.submissionStatus,
        frontCover: story.frontCover,
        backCover: story.backCover,
        pageImages: story.pageImages || [],
        generatedStory: story.generatedStory,
      });
    } catch {
      /* keep showing current pages */
    }
  }, [sessionIdFromQuery, getAuthHeader]);

  useEffect(() => {
    if (!storyData) return;

    const buildPageSequence = async () => {
      setBookDisplayReady(false);
      try {
        const pages: BookPage[] = [];

        const resolveImageUrl = async (
          image: CoverData | PageImage | undefined,
          requireEdited: boolean = false
        ): Promise<string | null> => {
          if (!image) return null;
          if (requireEdited && !image.editedImageUrl && !image.editedImageStorageKey) {
            return null;
          }
          return resolveStoryImageUrl(image, token);
        };

        const resolveCover = async (cover: CoverData | undefined) => resolveCoverImageUrl(cover, token);

        const frontCoverUrl = await resolveCover(storyData.frontCover);
        pages.push({
          type: "cover-front",
          imageUrl: frontCoverUrl || undefined,
          isSinglePage: true,
          accessibleDescription: storyData.frontCover?.accessibleDescription,
        });

        /* Match StoryResult / print flow: inner spread before first story pages */
        pages.push({ type: "blank" });
        pages.push({ type: "blank" });

        if (storyData.pageImages && storyData.pageImages.length > 0) {
          const imgs = storyData.pageImages;
          const pageNums = imgs
            .map((img) => coerceStoryPageNumber(img.pageNumber))
            .filter((n): n is number => n != null);
          const maxPageNum = pageNums.length > 0 ? Math.max(...pageNums) : 0;

          for (let pageNum = 1; pageNum <= maxPageNum; pageNum++) {
            const mainImg = findStoryPageSlotImage(imgs, pageNum, false);
            const sideImg = findStoryPageSlotImage(imgs, pageNum, true);
            const mainResolved = await resolveImageUrl(mainImg, false);
            const sideResolved = await resolveImageUrl(sideImg, false);
            const mainAlt =
              (typeof mainImg?.accessibleDescription === "string" ? mainImg.accessibleDescription : "").trim();
            const sideAlt =
              (typeof sideImg?.accessibleDescription === "string" ? sideImg.accessibleDescription : "").trim();

            if (mainResolved && sideResolved) {
              pages.push({
                type: "story",
                pageNumber: pageNum,
                imageUrl: mainResolved,
                isFullSpread: false,
                accessibleDescription: mainAlt || sideAlt,
              });
              pages.push({
                type: "story",
                pageNumber: pageNum,
                imageUrl: sideResolved,
                isFullSpread: false,
                accessibleDescription: sideAlt || mainAlt,
              });
            } else if (mainResolved || sideResolved) {
              const u = (mainResolved || sideResolved) as string;
              const alt = (mainResolved ? mainAlt : sideAlt) || mainAlt || sideAlt;
              pages.push({
                type: "story",
                pageNumber: pageNum,
                imageUrl: u,
                isFullSpread: false,
                accessibleDescription: alt,
              });
              pages.push({
                type: "story",
                pageNumber: pageNum,
                imageUrl: u,
                isFullSpread: false,
                accessibleDescription: alt,
              });
            }
          }
        }

        const backCoverUrl = await resolveCover(storyData.backCover);
        pages.push({
          type: "cover-back",
          imageUrl: backCoverUrl || undefined,
          isSinglePage: true,
          accessibleDescription: storyData.backCover?.accessibleDescription,
        });

        setPageSequence(pages);

        const viewCount = buildBookReaderViews(pages).length;

        const uniqueImageUrls = [...new Set(pages.map((p) => p.imageUrl).filter(Boolean))] as string[];
        const PRELOAD_TIMEOUT_MS = 20000;
        await Promise.all(
          uniqueImageUrls.map(
            (src) =>
              new Promise<void>((resolve) => {
                const img = new Image();
                const t = window.setTimeout(() => resolve(), PRELOAD_TIMEOUT_MS);
                const done = () => {
                  window.clearTimeout(t);
                  resolve();
                };
                img.onload = done;
                img.onerror = done;
                img.src = src;
                if (img.complete && img.naturalWidth > 0) {
                  done();
                }
              })
          )
        );

        /* Ensure URL ?p is present (1-based) */
        setSearchParams(
          (prev) => {
            const n = new URLSearchParams(prev);
            const raw = Number.parseInt(n.get("p") || "1", 10);
            const pSafe = Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1;
            const clampedPage = Math.min(pSafe, Math.max(1, viewCount));
            n.set("p", String(Math.max(1, clampedPage)));
            return n;
          },
          { replace: true }
        );
      } catch (e) {
        console.error("DigitalBookViewPage: buildPageSequence failed", e);
      } finally {
        setBookDisplayReady(true);
      }
    };

    buildPageSequence();
    // setSearchParams is stable; including it would be redundant and can churn with router internals.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follow [storyData, token] only
  }, [storyData, token]);

  const readerViews = useMemo(() => buildBookReaderViews(pageSequence), [pageSequence]);
  const readerViewCount = readerViews.length;

  const parsedUrlPage = Number.parseInt(searchParams.get("p") || "1", 10);
  const urlPageSafe = Number.isFinite(parsedUrlPage) && parsedUrlPage >= 1 ? Math.floor(parsedUrlPage) : 1;

  const pageIndex = Math.min(urlPageSafe, Math.max(1, readerViewCount)) - 1;

  const setCommittedPageIdx = useCallback(
    (idx: number) => {
      const nextIdx = clampIndex(idx, readerViewCount);
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.set("p", String(nextIdx + 1));
          return n;
        },
        { replace: true }
      );
    },
    [readerViewCount, setSearchParams]
  );

  function clampIndex(idx: number, len: number) {
    if (len <= 0) return 0;
    return Math.min(Math.max(0, idx), len - 1);
  }

  const coverTitleOverlay = storyData?.frontCover
    ? coverDisplayTitleFromOverlays(storyData.frontCover as { textOverlays?: unknown })
    : "";

  const exitHref = `/create/result?sessionId=${encodeURIComponent(sessionIdFromQuery)}`;
  const exitLabel = t("wizard.digitalBookView.backToStoryResult");

  if (isLoading || (storyData && !bookDisplayReady)) {
    return (
      <div className="relative flex min-h-screen w-full flex-col items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 px-4">
        <main id="main-content" tabIndex={-1} className="flex flex-col items-center outline-none" aria-busy="true">
          <LogoLoader size="lg" />
          <p className="mt-4 max-w-md text-center text-base font-semibold text-brandNavy">
            {isLoading ? t("wizard.storyResult.loadingStory") : t("wizard.storyResult.loadingBookImages")}
          </p>
        </main>
      </div>
    );
  }

  if (error || !storyData) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-amber-50 via-orange-100 to-amber-200 px-4">
        <p className="text-red-800" role="alert">
          {error || t("wizard.storyResult.storyNotFound")}
        </p>
        <Link
          to="/create/order-summary"
          className="mt-4 rounded-2xl bg-brandNavy px-6 py-3 text-sm font-semibold text-white shadow-card hover:bg-brandNavy/90"
        >
          {t("wizard.storyResult.backToRequestSummary")}
        </Link>
      </div>
    );
  }

  return (
    <DigitalBookReader
      sessionId={sessionIdFromQuery}
      pageSequence={pageSequence}
      storyLanguage={readingLanguage}
      generatedStory={storyData.generatedStory}
      coverDisplayTitle={coverTitleOverlay}
      uiLocale={uiLocale}
      setUiLocale={setUiLocale}
      pageIndex={readerViewCount === 0 ? 0 : pageIndex}
      setPageIndex={setCommittedPageIdx}
      exitHref={exitHref}
      exitLabel={exitLabel}
      adminSpreadSwap={
        user?.isAdmin === true
          ? {
              getAuthHeader,
              refetchStory: refetchStoryForBook,
            }
          : undefined
      }
    />
  );
};

export default DigitalBookViewPage;
